/**
 * Shared Apollo controller for Table/Pitch watchlist preference ordering.
 *
 * The existing Pitch projection supplies the authoritative revision and full
 * player order. The write document is kept locally typed until the deployed
 * schema can regenerate the repository-wide GraphQL artifact.
 */
import { useMutation } from "@apollo/client";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import {
  type PitchFormation,
  ScoutingUpdateWatchlistFormationDocument,
  type ScoutingUpdateWatchlistFormationMutation,
  type ScoutingUpdateWatchlistFormationMutationVariables,
  ScoutingUpdateWatchlistPitchAssignmentDocument,
  type ScoutingUpdateWatchlistPitchAssignmentMutation,
  type ScoutingUpdateWatchlistPitchAssignmentMutationVariables,
  type TacticalSlot,
  useScoutingGetWatchlistPitchQuery,
} from "@/generated/graphql";

import {
  applyRelativePreferenceOperation,
  createPitchAssignmentOperation,
  createPreferenceOperation,
  createPreferenceOrderState,
  projectPreferenceOrder,
  projectPitchAssignments,
  projectPitchFormation,
  projectPitchFormerSlots,
  type WatchlistPitchOperation,
  type WatchlistPreferenceFailure,
  type WatchlistPreferenceSnapshot,
  type WatchlistPreferenceStatus,
} from "./watchlistPreferenceOrderState";
import { preferenceOrderReducer } from "./watchlistPreferenceOrderReducer";
import { useWatchlistPreferenceRecovery } from "./useWatchlistPreferenceRecovery";
import {
  formationSlots,
  pendingOperationBase,
  type PitchWriteExecutor,
  settlePendingOperations,
  supersededOperationIds,
} from "./watchlistPreferenceOrderQueue";
import {
  snapshotFromAssignmentPayload,
  toSnapshot,
  watchlistFormationOperation,
} from "./watchlistPreferenceOrderSnapshots";
import {
  appendWatchlistPitchPage,
  hasWatchlistPitchConflictCode,
  PITCH_PAGE_SIZE,
  readCompleteWatchlistPitch,
  readMutationErrorMessage,
  readMutationPayload,
  UPDATE_WATCHLIST_PREFERENCE_ORDER,
  type UpdatePreferenceOrderData,
  type UpdatePreferenceOrderVariables,
} from "./watchlistPreferenceOrderGraphql";

export { appendWatchlistPitchPage } from "./watchlistPreferenceOrderGraphql";
export { formationSlots } from "./watchlistPreferenceOrderQueue";

/** Public controller consumed by both visual representations. */
export interface WatchlistPreferenceOrderController {
  readonly orderedPlayerIds: readonly string[];
  readonly pitchRevision: number;
  readonly manualOrder: boolean;
  readonly canEdit: boolean;
  readonly paused: boolean;
  readonly status: WatchlistPreferenceStatus;
  readonly message?: string;
  readonly rankFor: (playerId: string) => number | undefined;
  readonly slotFor: (playerId: string) => TacticalSlot | null | undefined;
  /**
   * The slot an Unassigned member held before the formation change that
   * orphaned them (SE-8391); undefined once placed or when never orphaned.
   */
  readonly formerSlotFor: (playerId: string) => TacticalSlot | undefined;
  /** Effective formation including an optimistic change, once a read exists. */
  readonly formation?: PitchFormation;
  readonly assignPlayer: (
    playerId: string,
    tacticalSlot: TacticalSlot | null
  ) => Promise<void>;
  readonly changeFormation: (formation: PitchFormation) => Promise<void>;
  readonly reorderVisible: (
    orderedPlayerIds: readonly string[]
  ) => Promise<void>;
  readonly refresh: () => Promise<void>;
  /** Epoch milliseconds of the last successful save (SE-8389 AC7). */
  readonly savedAt?: number;
  /** The operation kept on screen after its save failed (AC1–AC3). */
  readonly failure?: WatchlistPreferenceFailure;
  /** That failed operation itself, for the banner's change summary. */
  readonly failedChange?: WatchlistPitchOperation;
  /** The change a conflict threw out, kept as a copy to re-apply (D17). */
  readonly heldChange?: WatchlistPitchOperation;
  /** Backend message of a refused re-apply (AC6). */
  readonly reapplyRefusal?: string;
  /** Re-sends the failed operation once with the current revision. */
  readonly retry: () => Promise<void>;
  /** Rolls back the failed operation only. */
  readonly discard: () => void;
  /** "Reload their version": drops the held copy. */
  readonly reload: () => void;
  /** "Re-apply my change": sends the held copy once with the new revision. */
  readonly reapply: () => Promise<void>;
}

/** Inputs that identify and optionally pause one watchlist controller. */
interface UseWatchlistPreferenceOrderOptions {
  readonly watchlistId: string | null;
  readonly paused?: boolean;
}

/**
 * Owns the one preference-order state for the selected watchlist.
 * @param options - Selected identity and whether an explicit sort pauses edits
 * @param options.watchlistId - Selected watchlist identity, when one is active
 * @param options.paused - Whether an explicit table sort pauses order editing
 * @returns Shared Table/Pitch order, ranks and operation-scoped CAS writer
 */
export const useWatchlistPreferenceOrder = ({
  watchlistId,
  paused = false,
}: UseWatchlistPreferenceOrderOptions): WatchlistPreferenceOrderController => {
  const stableWatchlistId = watchlistId ?? "";
  const [state, dispatch] = useReducer(
    preferenceOrderReducer,
    stableWatchlistId,
    createPreferenceOrderState
  );
  const operationSequence = useRef(0);
  const selectedWatchlistId = useRef(stableWatchlistId);
  const confirmedSnapshots = useRef(
    new Map<string, WatchlistPreferenceSnapshot>()
  );
  const [rememberedSnapshots, setRememberedSnapshots] = useState<
    Readonly<Record<string, WatchlistPreferenceSnapshot>>
  >({});
  const writeQueues = useRef(new Map<string, Promise<void>>());
  const pendingOperations = useRef(
    new Map<string, WatchlistPitchOperation[]>()
  );
  const paginationCursor = useRef<string | null>(null);
  const [updateOrder] = useMutation<
    UpdatePreferenceOrderData,
    UpdatePreferenceOrderVariables
  >(UPDATE_WATCHLIST_PREFERENCE_ORDER);
  const [updateAssignment] = useMutation<
    ScoutingUpdateWatchlistPitchAssignmentMutation,
    ScoutingUpdateWatchlistPitchAssignmentMutationVariables
  >(ScoutingUpdateWatchlistPitchAssignmentDocument);
  const [updateFormation] = useMutation<
    ScoutingUpdateWatchlistFormationMutation,
    ScoutingUpdateWatchlistFormationMutationVariables
  >(ScoutingUpdateWatchlistFormationDocument);
  const { data, refetch, fetchMore } = useScoutingGetWatchlistPitchQuery({
    variables: { id: stableWatchlistId, first: PITCH_PAGE_SIZE },
    skip: !watchlistId,
    notifyOnNetworkStatusChange: true,
    // The controller loads an unfiltered global order. Keeping it outside the
    // normalized cache prevents its page from merging into the filtered or
    // paginated Pitch connection rendered by WatchlistPitchContainer.
    fetchPolicy: "no-cache",
  });
  const refetchCompletePitch = useCallback(
    (id: string) =>
      readCompleteWatchlistPitch({
        id,
        pageSize: PITCH_PAGE_SIZE,
        refetch,
        fetchPage: after => {
          paginationCursor.current = after;
          return fetchMore({
            variables: { id, first: PITCH_PAGE_SIZE, after },
          });
        },
      }),
    [fetchMore, refetch]
  );
  const pitch = data?.getWatchlistPitch;
  const currentState =
    state.watchlistId === stableWatchlistId
      ? state
      : createPreferenceOrderState(stableWatchlistId);
  const pitchSnapshot = useMemo(
    () => (pitch ? toSnapshot(pitch) : undefined),
    [pitch]
  );
  const rememberedSnapshot = rememberedSnapshots[stableWatchlistId];
  const fallbackSnapshot =
    rememberedSnapshot &&
    (!pitchSnapshot ||
      rememberedSnapshot.pitchRevision > pitchSnapshot.pitchRevision)
      ? rememberedSnapshot
      : (pitchSnapshot ?? currentState.confirmed);
  const projectedState = useMemo(
    () =>
      currentState.confirmed.orderedPlayerIds.length === 0 && pitch
        ? { ...currentState, confirmed: fallbackSnapshot }
        : currentState,
    [currentState, fallbackSnapshot, pitch]
  );
  const orderedPlayerIds = useMemo(
    () => projectPreferenceOrder(projectedState),
    [projectedState]
  );

  // SE-8389: the request behind each operation outlives one attempt — Retry
  // re-runs it against the current revision and Re-apply against the one the
  // conflict adopted; the closure reads `confirmed` when it runs, never before.
  const executors = useRef(new Map<string, PitchWriteExecutor>());
  const reapplyingOperationId = useRef<string | undefined>(undefined);

  const settlePending = useCallback(
    (targetWatchlistId: string, operationId: string, through: boolean) => {
      const pending = pendingOperations.current.get(targetWatchlistId) ?? [];
      // A snapshot supersedes every older request too: release their
      // executors, keeping this operation's own for a possible Re-apply.
      supersededOperationIds(pending, operationId, through).forEach(id =>
        executors.current.delete(id)
      );
      pendingOperations.current.set(targetWatchlistId, [
        ...settlePendingOperations(pending, operationId, through),
      ]);
    },
    []
  );

  const runPitchWrite = useCallback(
    async (targetWatchlistId: string, operation: WatchlistPitchOperation) => {
      const operationId = operation.id;
      const executor = executors.current.get(operationId);
      if (!executor) return;
      if (!confirmedSnapshots.current.has(targetWatchlistId)) {
        confirmedSnapshots.current.set(
          targetWatchlistId,
          projectedState.confirmed
        );
      }
      const adopt = (snapshot: WatchlistPreferenceSnapshot) => {
        confirmedSnapshots.current.set(targetWatchlistId, snapshot);
        setRememberedSnapshots(previous => ({
          ...previous,
          [targetWatchlistId]: snapshot,
        }));
        settlePending(targetWatchlistId, operationId, true);
      };
      const previousWrite = writeQueues.current.get(targetWatchlistId);
      const write = (previousWrite ?? Promise.resolve()).then(async () => {
        try {
          const confirmed = confirmedSnapshots.current.get(targetWatchlistId);
          if (!confirmed) throw new Error("Pitch snapshot was unavailable");
          const snapshot = await executor.execute(confirmed);
          adopt(snapshot);
          executors.current.delete(operationId);
          // Writes are serialized per list, so a re-apply in flight is this
          // one; the conflict branch resets the marker the same way.
          reapplyingOperationId.current = undefined;
          dispatch({
            type: "success",
            watchlistId: targetWatchlistId,
            operationId,
            snapshot,
            savedAt: Date.now(),
          });
        } catch (error) {
          if (
            hasWatchlistPitchConflictCode(error) &&
            selectedWatchlistId.current === targetWatchlistId
          ) {
            try {
              const latest = await refetchCompletePitch(targetWatchlistId);
              if (
                latest?.id === targetWatchlistId &&
                selectedWatchlistId.current === targetWatchlistId
              ) {
                const snapshot = toSnapshot(latest);
                adopt(snapshot);
                reapplyingOperationId.current = undefined;
                dispatch({
                  type: "conflict",
                  watchlistId: targetWatchlistId,
                  operationId,
                  snapshot,
                });
                return;
              }
            } catch {
              // Fall through: the change stays on screen with Retry.
            }
          }
          if (reapplyingOperationId.current === operationId) {
            // A refused re-apply discards the copy; nothing is retried (D17).
            reapplyingOperationId.current = undefined;
            executors.current.delete(operationId);
            settlePending(targetWatchlistId, operationId, false);
          }
          dispatch({
            type: "failure",
            watchlistId: targetWatchlistId,
            operationId,
            message: readMutationErrorMessage(error),
          });
        }
      });
      writeQueues.current.set(targetWatchlistId, write);
      await write;
      if (writeQueues.current.get(targetWatchlistId) === write) {
        writeQueues.current.delete(targetWatchlistId);
      }
    },
    [projectedState.confirmed, refetchCompletePitch, settlePending]
  );

  const commitPitchWrite = useCallback(
    async (
      targetWatchlistId: string,
      operation: WatchlistPitchOperation,
      execute: PitchWriteExecutor["execute"]
    ) => {
      pendingOperations.current.set(targetWatchlistId, [
        ...(pendingOperations.current.get(targetWatchlistId) ?? []),
        operation,
      ]);
      executors.current.set(operation.id, { execute });
      dispatch({ type: "start", watchlistId: targetWatchlistId, operation });
      await runPitchWrite(targetWatchlistId, operation);
    },
    [runPitchWrite]
  );

  const markReapplying = useCallback((operationId: string) => {
    reapplyingOperationId.current = operationId;
  }, []);
  const { failedOperation, retry, discard, reload, reapply } =
    useWatchlistPreferenceRecovery({
      watchlistId,
      projectedState,
      dispatch,
      runPitchWrite,
      settlePending,
      executors,
      pendingOperations,
      markReapplying,
    });

  const reorderVisible = useCallback(
    async (requestedPlayerIds: readonly string[]) => {
      const requested = new Set(requestedPlayerIds);
      if (
        !watchlistId ||
        !pitch?.canEdit ||
        pitch.members.pageInfo.hasNextPage ||
        paused ||
        requestedPlayerIds.length === 0 ||
        requested.size !== requestedPlayerIds.length ||
        requestedPlayerIds.some(
          playerId =>
            !projectedState.confirmed.orderedPlayerIds.includes(playerId)
        )
      )
        return;
      const operationId = `${watchlistId}:${++operationSequence.current}`;
      const confirmedForWatchlist =
        confirmedSnapshots.current.get(watchlistId) ?? projectedState.confirmed;
      const operationBase = pendingOperationBase(
        confirmedForWatchlist.orderedPlayerIds,
        pendingOperations.current.get(watchlistId) ?? []
      );
      const operation = createPreferenceOperation(
        operationId,
        operationBase,
        requestedPlayerIds
      );
      await commitPitchWrite(watchlistId, operation, async confirmed => {
        const rebasedOrder = applyRelativePreferenceOperation(
          confirmed.orderedPlayerIds,
          operation
        );
        const operationPlayerIds = new Set(operation.playerIds);
        const result = await updateOrder({
          variables: {
            input: {
              watchlistId,
              expectedPitchRevision: confirmed.pitchRevision,
              orderedPlayerIds: rebasedOrder.filter(playerId =>
                operationPlayerIds.has(playerId)
              ),
            },
          },
          refetchQueries: [
            "ScoutingListAllTargets",
            "ScoutingGetWatchlistPitch",
          ],
        });
        const payload = readMutationPayload(
          result.data?.updateWatchlistPreferenceOrder,
          result.errors,
          "Preference order response was empty"
        );
        return {
          ...toSnapshot(payload),
          assignmentsByPlayerId: confirmed.assignmentsByPlayerId,
          pitchInitialized: confirmed.pitchInitialized,
          formation: confirmed.formation,
        };
      });
    },
    [
      commitPitchWrite,
      paused,
      pitch,
      projectedState.confirmed,
      updateOrder,
      watchlistId,
    ]
  );

  const assignPlayer = useCallback(
    async (playerId: string, tacticalSlot: TacticalSlot | null) => {
      if (
        !watchlistId ||
        !pitch?.canEdit ||
        pitch.members.pageInfo.hasNextPage ||
        !projectedState.confirmed.orderedPlayerIds.includes(playerId)
      )
        return;
      const operation = createPitchAssignmentOperation(
        `${watchlistId}:${++operationSequence.current}`,
        playerId,
        tacticalSlot
      );
      await commitPitchWrite(watchlistId, operation, async confirmed => {
        const result = await updateAssignment({
          variables: {
            input: {
              watchlistId,
              playerId,
              tacticalSlot,
              expectedPitchRevision: confirmed.pitchRevision,
            },
          },
          refetchQueries: [
            "ScoutingListAllTargets",
            "ScoutingGetWatchlistPitch",
          ],
        });
        const payload = readMutationPayload(
          result.data?.updateWatchlistPitchAssignment,
          result.errors,
          "Pitch assignment response was empty"
        );
        return snapshotFromAssignmentPayload(confirmed, payload);
      });
    },
    [
      commitPitchWrite,
      pitch,
      projectedState.confirmed,
      updateAssignment,
      watchlistId,
    ]
  );

  const changeFormation = useCallback(
    async (nextFormation: PitchFormation) => {
      if (
        !watchlistId ||
        !pitch?.canEdit ||
        pitch.members.pageInfo.hasNextPage ||
        projectPitchFormation(projectedState) === nextFormation
      )
        return;
      const operation = watchlistFormationOperation(
        `${watchlistId}:${++operationSequence.current}`,
        nextFormation,
        formationSlots(nextFormation),
        projectPitchAssignments(projectedState),
        pitch.members.edges
      );
      await commitPitchWrite(watchlistId, operation, async confirmed => {
        const result = await updateFormation({
          variables: {
            input: {
              watchlistId,
              formation: nextFormation,
              expectedPitchRevision: confirmed.pitchRevision,
            },
          },
          refetchQueries: [
            "ScoutingListAllTargets",
            "ScoutingGetWatchlistPitch",
          ],
        });
        const payload = readMutationPayload(
          result.data?.updateWatchlistFormation,
          result.errors,
          "Formation response was empty"
        );
        return snapshotFromAssignmentPayload(confirmed, payload);
      });
    },
    [commitPitchWrite, pitch, projectedState, updateFormation, watchlistId]
  );

  const formation = useMemo(
    () => projectPitchFormation(projectedState) as PitchFormation | undefined,
    [projectedState]
  );

  const rankByPlayerId = useMemo(
    () =>
      new Map(orderedPlayerIds.map((playerId, index) => [playerId, index + 1])),
    [orderedPlayerIds]
  );
  const rankFor = useCallback(
    (playerId: string) => rankByPlayerId.get(playerId),
    [rankByPlayerId]
  );
  const assignmentsByPlayerId = useMemo(
    () => projectPitchAssignments(projectedState),
    [projectedState]
  );
  const slotFor = useCallback(
    (playerId: string) =>
      assignmentsByPlayerId[playerId]?.tacticalSlot as
        | TacticalSlot
        | null
        | undefined,
    [assignmentsByPlayerId]
  );
  const formerSlotByPlayerId = useMemo(
    () => projectPitchFormerSlots(projectedState),
    [projectedState]
  );
  const formerSlotFor = useCallback(
    (playerId: string) =>
      formerSlotByPlayerId[playerId] as TacticalSlot | undefined,
    [formerSlotByPlayerId]
  );
  const refresh = useCallback(async () => {
    if (!watchlistId) return;
    await refetch();
  }, [refetch, watchlistId]);

  const canEdit = Boolean(
    pitch?.canEdit && !pitch.members.pageInfo.hasNextPage
  );
  useLayoutEffect(() => {
    selectedWatchlistId.current = stableWatchlistId;
  }, [stableWatchlistId]);
  useEffect(() => {
    paginationCursor.current = null;
  }, [stableWatchlistId]);
  useEffect(() => {
    if (!pitch) return;
    const snapshot = toSnapshot(pitch);
    const known = confirmedSnapshots.current.get(pitch.id);
    const authoritative =
      known &&
      (known.pitchRevision > snapshot.pitchRevision ||
        (known.pitchRevision === snapshot.pitchRevision &&
          known.orderedPlayerIds.length >= snapshot.orderedPlayerIds.length))
        ? known
        : snapshot;
    confirmedSnapshots.current.set(pitch.id, authoritative);
    setRememberedSnapshots(previous =>
      previous[pitch.id] === authoritative
        ? previous
        : { ...previous, [pitch.id]: authoritative }
    );
    dispatch({
      type: "hydrate",
      watchlistId: pitch.id,
      snapshot: authoritative,
    });
  }, [pitch]);
  useEffect(() => {
    const pageInfo = pitch?.members.pageInfo;
    const after = pageInfo?.endCursor;
    if (
      !pageInfo?.hasNextPage ||
      !after ||
      paginationCursor.current === after
    ) {
      return;
    }
    paginationCursor.current = after;
    void fetchMore({
      variables: { after },
      updateQuery: (previous, { fetchMoreResult }) =>
        appendWatchlistPitchPage(previous, fetchMoreResult),
    }).catch(() => {
      if (paginationCursor.current === after) paginationCursor.current = null;
    });
  }, [fetchMore, pitch?.members.pageInfo]);

  // One controller identity per state change: consumers memoize on the
  // controller object (rows, overlays, handlers), so a fresh object on every
  // render would rebuild their outputs on every render as well.
  return useMemo(
    () => ({
      orderedPlayerIds,
      pitchRevision: projectedState.confirmed.pitchRevision,
      manualOrder: projectedState.confirmed.manualOrder,
      canEdit,
      paused,
      status: projectedState.status,
      message: projectedState.message,
      rankFor,
      slotFor,
      formerSlotFor,
      formation,
      assignPlayer,
      changeFormation,
      reorderVisible,
      refresh,
      savedAt: projectedState.savedAt,
      failure: projectedState.failure,
      failedChange: failedOperation,
      heldChange: projectedState.heldChange,
      reapplyRefusal: projectedState.reapplyRefusal,
      retry,
      discard,
      reload,
      reapply,
    }),
    [
      assignPlayer,
      canEdit,
      changeFormation,
      discard,
      failedOperation,
      formation,
      formerSlotFor,
      orderedPlayerIds,
      paused,
      projectedState,
      rankFor,
      reapply,
      refresh,
      reload,
      reorderVisible,
      retry,
      slotFor,
    ]
  );
};

export { moveVisiblePlayer } from "./watchlistPreferenceOrderState";
